/**
 * @module utils/url
 * @description URL validation and sanitization utilities.
 * Provides query parameter validation with context-specific allowed filters
 * for browser and server environments.
 *
 * @requires constants/catalog - Imports SEARCH_SORT_OPTIONS for sort validation
 */

import { SEARCH_SORT_OPTIONS } from 'constants/catalog';

/**
 * @constant {string[]} ALLOWED_FILTERS_BASE - Base allowed query parameters for both environments
 * @description Common query parameters permitted in URLs across all contexts
 */
const ALLOWED_FILTERS_BASE = ['currentPage', 'pageSize', 'ph', 'pl', 'ref', 'sortBy', 'ptype', 'node'];

/**
 * @constant {string[]} ALLOWED_FILTERS_BROWSER - Allowed query parameters for browser context
 * @description Includes base filters plus browser-specific parameters like 'icid2' for tracking
 */
const ALLOWED_FILTERS_BROWSER = [...ALLOWED_FILTERS_BASE, 'icid2'];

/**
 * @constant {string[]} ALLOWED_FILTERS_SERVER - Allowed query parameters for server context
 * @description Currently identical to base filters, separated for potential server-specific rules
 */
const ALLOWED_FILTERS_SERVER = [...ALLOWED_FILTERS_BASE];

/**
 * Validates and sanitizes a query parameter
 * @function validateQueryParams
 * @param {string} param - The parameter name to validate
 * @param {string} value - The parameter value to validate
 * @param {string[]} allowedFilters - Array of allowed parameter names for the current context
 * @returns {string|''} Sanitized query string (e.g., "param=value") or empty string if invalid
 * @description Validates query parameters against allowed filters and regex patterns.
 * Different parameters have specific validation rules:
 * - 'ref', 'node', 'pl', 'ph', 'currentPage', 'pageSize', 'icid2': Pattern-based validation
 * - 'ptype': Must be 'manual'
 * - 'sortBy': Must match a valid SEARCH_SORT_OPTIONS code
 *
 * @example
 * validateQueryParams('currentPage', '2', ALLOWED_FILTERS_BROWSER) // 'currentPage=2'
 * validateQueryParams('sortBy', 'RELEVANCY', ALLOWED_FILTERS_BROWSER) // 'sortBy=RELEVANCY'
 * validateQueryParams('invalid', 'value', ALLOWED_FILTERS_BROWSER) // undefined
 */
const validateQueryParams = function (param: string, value: string, allowedFilters: string[]): string | undefined {
    // eslint-disable-next-line no-useless-escape
    const stringRegEx = /^[A-Za-zÀ-Ÿ (\-",')_?&\[\]=\d]*$/g;
    const numberRegEx = /^(0|[1-9]\d*)$/g;
    const priceRangeRegEx = /^(min|max|(0|[1-9]\d*))$/g;
    const regExObj: Record<string, RegExp> = {
        ref: stringRegEx,
        node: /^cat[0-9]+$/g,
        pl: priceRangeRegEx,
        ph: priceRangeRegEx,
        currentPage: numberRegEx,
        pageSize: numberRegEx,
        icid2: stringRegEx
    };
    let sanitizedQueryParam: string = '';

    if (allowedFilters.indexOf(param) !== -1) {
        if (param === 'ptype' && value === 'manual') {
            sanitizedQueryParam = `${param}=manual`;
        }

        if (param === 'sortBy') {
            if (SEARCH_SORT_OPTIONS.find(e => e.code === value)) {
                sanitizedQueryParam = `sortBy=${value}`;
            }
        }

        if (param === 'ref') {
            try {
                if (regExObj.ref.test(decodeURIComponent(value))) {
                    sanitizedQueryParam = `ref=${value}`;
                }
            } catch (_e) {
                console.error('Error decodeURIComponent URL ref param!'); // eslint-disable-line
            }
        }

        if (param !== 'ptype' && param !== 'sortBy' && param !== 'ref') {
            if (regExObj[param].test(value)) {
                sanitizedQueryParam = `${param}=${value}`;
            }
        }
    }

    return sanitizedQueryParam;
};

export { validateQueryParams, ALLOWED_FILTERS_SERVER, ALLOWED_FILTERS_BROWSER };
